import { useEffect, useState } from "react";
import { ClimateRiskReport } from "@/components/assessment/report/ClimateRiskReport";
import type { Workspace } from "@/lib/auth";
import { buildReport, type ReportModel } from "@/lib/report";
import { loadReportSources } from "@/lib/report-data";
import { defaultResultsFilterState } from "@/lib/results-filters";
import { decodeFiltersParam } from "@shared/report-filters";

declare global {
  interface Window {
    /** Set when the report is fully rendered and fonts are confirmed; the server waits on it. */
    __REPORT_READY__?: boolean;
    /** Set on any failure so the server fails in seconds instead of timing out. */
    __REPORT_ERROR__?: string;
  }
}

/**
 * First writer wins, enforced in BOTH directions — the server resolves its race
 * and *then* reads __REPORT_ERROR__, so a late stray error after a good render
 * would 502 a healthy report. Never assign either flag directly.
 */
function reportError(message: string) {
  if (window.__REPORT_READY__ || typeof window.__REPORT_ERROR__ === "string") return;
  window.__REPORT_ERROR__ = message;
}

function reportReady() {
  if (typeof window.__REPORT_ERROR__ === "string") return;
  window.__REPORT_READY__ = true;
}

const FONT_CHECK_TIMEOUT_MS = 5_000;
const IMAGE_CHECK_TIMEOUT_MS = 5_000;

/**
 * document.fonts.check() returns true when no @font-face matches, so it can't
 * detect a missing font; load() resolves to the matched faces, which is empty
 * when the family is absent. Fail loudly rather than emit a wrong-font PDF.
 *
 * Raced against a timer: load() rejects on a failed fetch (caught below), but a
 * *stalled* fetch would never settle, and with no other signal the server would
 * sit out its whole 30 s budget for what is really a font problem.
 */
async function reportFontsLoaded(): Promise<boolean> {
  const timeout = new Promise<false>((resolve) => setTimeout(() => resolve(false), FONT_CHECK_TIMEOUT_MS));
  const check = (async () => {
    try {
      const [dmSans, outfit] = await Promise.all([
        document.fonts.load('400 16px "DM Sans"'),
        document.fonts.load('600 16px "Outfit"'),
      ]);
      await document.fonts.ready;
      return dmSans.length > 0 && outfit.length > 0;
    } catch {
      return false;
    }
  })();
  return Promise.race([check, timeout]);
}

/**
 * The cover's logo is a bitmap, and neither page.goto's "domcontentloaded" nor
 * page.pdf() waits for images — without this, an export can ship with the mark
 * silently missing. Mirrors reportFontsLoaded's shape: raced against a timer so
 * a stalled image can never eat the server's render budget.
 *
 * Unlike fonts this does NOT fail the render. A wrong font corrupts every glyph
 * in the document, so that one is worth a 502; one missing image is not worth
 * denying someone a report they need.
 */
async function reportImagesDecoded(): Promise<void> {
  const decoded = Promise.all(
    Array.from(document.images).map((image) => image.decode().catch(() => undefined)),
  );
  await Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, IMAGE_CHECK_TIMEOUT_MS))]);
}

/**
 * Rendered only by headless Chromium (POST /api/report/pdf). Not linked from
 * the UI; a logged-in user opening it sees the report unframed — nothing they
 * can't already see.
 *
 * Dev-only note: main.tsx wraps the app in <StrictMode>, so in `npm run dev`
 * every effect here runs twice and this route calls /api/auth/me and the four
 * data endpoints twice — /api/results is computed 3× per export, not the 2×
 * the spec budgets. That is a dev artifact, not a leak; the built server does 2×.
 */
function ReportPrint() {
  const [model, setModel] = useState<ReportModel | null>(null);

  // A throw while rendering the report subtree would run no effect at all, so
  // neither flag would ever be set and the server would burn its full 30 s for
  // no diagnostic. These listeners are the backstop for that.
  useEffect(() => {
    const onError = (event: ErrorEvent) => reportError(event.message || "The report failed to render");
    // Carry the real reason: unhandledrejection is a broad net (in dev, Vite's
    // HMR client and React's dev build can emit stray rejections), and a generic
    // message would be indistinguishable from a genuine failure in the 502.
    const onRejection = (event: PromiseRejectionEvent) =>
      reportError(event.reason instanceof Error ? event.reason.message : "The report failed to render");
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Plain fetch, not request(): request() hard-redirects to /login on a 401, and the server can't see that.
      const meRes = await fetch("/api/auth/me");
      if (!meRes.ok) throw new Error("Not authenticated");
      const me = (await meRes.json()) as { workspace: Workspace | null };

      const filters = decodeFiltersParam(new URLSearchParams(window.location.search).get("f")) ?? defaultResultsFilterState();
      const sources = await loadReportSources();
      if (!cancelled) setModel(buildReport({ workspaceName: me.workspace?.name ?? "", ...sources, filters }));
    })().catch((err) => {
      // Guarded by `cancelled`: <StrictMode> double-invokes effects in dev, and
      // an aborted first run must not report an error the second run recovers
      // from — the server reads __REPORT_ERROR__ after the race, so a stale
      // error would 502 an otherwise healthy render.
      if (!cancelled) reportError(err instanceof Error ? err.message : "The report failed to load");
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Runs after the model has been committed to the DOM.
  useEffect(() => {
    if (!model) return;
    let cancelled = false;

    (async () => {
      const [fontsOk] = await Promise.all([reportFontsLoaded(), reportImagesDecoded()]);
      // Double rAF (a single one fires before paint, not after), but raced
      // against a timer: rAF is not guaranteed to fire on a hidden or occluded
      // page, and the renderer launches with --disable-gpu. Without the timer,
      // a suppressed frame would leave BOTH flags unset — a full 30 s timeout
      // for a render that was fine. page.pdf() lays out on its own, so the rAF
      // is a nicety and the timer is the guarantee.
      await Promise.race([
        new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))),
        new Promise((resolve) => setTimeout(resolve, 250)),
      ]);
      if (cancelled) return;
      if (fontsOk) reportReady();
      else reportError("Report fonts failed to load");
    })();

    return () => {
      cancelled = true;
    };
  }, [model]);

  if (!model) return null;

  return (
    <div className="bg-white">
      <ClimateRiskReport model={model} variant="pdf" />
    </div>
  );
}

export default ReportPrint;
